/**
 * Production-Safe Logger
 * 
 * HIPAA/GDPR Compliant logging utility that:
 * - Redacts PII in production
 * - Disables debug logs in production
 * - Sends errors to monitoring service
 * - Maintains audit trail for security events
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'security';

interface LogContext {
  userId?: string;
  organizationId?: string;
  action?: string;
  resource?: string;
  [key: string]: unknown;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private enabledLevels: Set<LogLevel>;

  constructor() {
    // In production, only log warn, error, and security events
    this.enabledLevels = this.isDevelopment
      ? new Set(['debug', 'info', 'warn', 'error', 'security'])
      : new Set(['warn', 'error', 'security']);
  }

  /**
   * Redact sensitive information from logs
   */
  private redactPII(data: unknown): unknown {
    if (this.isDevelopment) {
      return data; // Show everything in dev
    }

    if (typeof data === 'string') {
      return data
        .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL_REDACTED]')
        .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[SSN_REDACTED]')
        .replace(/\b\d{16}\b/g, '[CARD_REDACTED]')
        .replace(/\b\d{3}-\d{3}-\d{4}\b/g, '[PHONE_REDACTED]');
    }

    if (typeof data === 'object' && data !== null) {
      const redacted: Record<string, unknown> = {};
      const sensitiveKeys = ['email', 'ssn', 'password', 'token', 'apiKey', 'creditCard', 'phone', 'address'];

      for (const [key, value] of Object.entries(data)) {
        if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
          redacted[key] = '[REDACTED]';
        } else {
          redacted[key] = this.redactPII(value);
        }
      }
      return redacted;
    }

    return data;
  }

  /**
   * Format log message with context
   */
  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` | ${JSON.stringify(this.redactPII(context))}` : '';
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
  }

  /**
   * Send log to external monitoring service (PostHog, Sentry, etc.)
   */
  private async sendToMonitoring(level: LogLevel, message: string, context?: LogContext, error?: Error) {
    if (!this.isDevelopment && (level === 'error' || level === 'security')) {
      try {
        // Integration point for external monitoring
        // Example: Sentry, PostHog, DataDog, etc.
        const posthog = (window as Window & {
          posthog?: { capture?: (event: string, properties?: Record<string, unknown>) => void };
        }).posthog;
        if (posthog) {
          posthog.capture('log_event', {
            level,
            message,
            context: this.redactPII(context),
            error: error?.message,
            stack: error?.stack,
          });
        }
      } catch (e) {
        // Fail silently to avoid breaking app
      }
    }
  }

  /**
   * Debug logs - only in development
   */
  debug(message: string, context?: LogContext) {
    if (!this.enabledLevels.has('debug')) return;
    console.log(this.formatMessage('debug', message, context));
  }

  /**
   * Info logs - general information
   */
  info(message: string, context?: LogContext) {
    if (!this.enabledLevels.has('info')) return;
    console.log(this.formatMessage('info', message, context));
  }

  /**
   * Warning logs - potential issues
   */
  warn(message: string, context?: LogContext) {
    if (!this.enabledLevels.has('warn')) return;
    console.warn(this.formatMessage('warn', message, context));
    this.sendToMonitoring('warn', message, context);
  }

  /**
   * Error logs - critical issues
   */
  error(message: string, error?: Error, context?: LogContext) {
    if (!this.enabledLevels.has('error')) return;
    const fullContext = { ...context, error: error?.message, stack: error?.stack };
    console.error(this.formatMessage('error', message, fullContext), error);
    this.sendToMonitoring('error', message, fullContext, error);
  }

  /**
   * Security logs - ALWAYS logged for audit trail
   */
  security(message: string, context?: LogContext) {
    // Security events are ALWAYS logged regardless of environment
    const formattedMessage = this.formatMessage('security', message, context);
    console.warn(`🔒 ${formattedMessage}`);
    this.sendToMonitoring('security', message, context);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for external use
export type { LogContext, LogLevel };
